import React, {memo, useEffect, useMemo} from 'react';

import {
  overrideLineColors,
  overrideLineTitles,
  overrideLineWidths,
  overrideMarks,
} from '../../../util/plotHelpers/chart';
import {Line} from '../../../util/plotHelpers/types';
import {usePanelContext} from '../../Panel/PanelContextProvider';
import {useSharedPanelState} from '../../Panel/SharedPanelStateContext';
import {useIsRenderingInPopup} from '../../Panel/SinglePanelInspectorContainer';
import {PanelPopupMode} from '../../Panel/types';
import {useDerivedLinesConfiguration} from '../config/useDerivedLinesConfiguration';
import {usePanelConfigContext} from '../PanelConfigContext';
import {startPerfTimer} from './../../../util/profiler';
import {useBucketedData} from './../bucketedData/useBucketedData';
import {ConfigWrapper} from './../Config';
import {GraphWrapper} from './../Graph';
import {usePanelData} from './../PanelDataContext';
import {usePanelTimeContext} from './../PanelTimeContext';
import {RunsLinePlotPanelProps} from './../types';
// import {useGorillaOutliers} from './../useGorillaOutliers';
import {useLines} from './useLines';

type PanelRunsLinePlotProps = RunsLinePlotPanelProps;

const featureSupport = {
  grouping: false,
};

/**
 * WIP: This component is being developed as part of the outliers feature. It is currently behind a feature and beta flag and is not ready for production use. In the meantime there will be `@ts-ignore` comments and `TODO` comments as we bring it to parity with existing runs line plots.
 */
const PanelOutliersRunsLinePlotComp: React.FC<
  PanelRunsLinePlotProps
> = props => {
  const {timeFactor} = usePanelTimeContext();

  const {config: runsLinePlotConfig} = props;

  const {isSingleRun} = usePanelConfigContext();

  const {handleSetHistoryData} = usePanelData();
  const {xDomainQuery} = useSharedPanelState();

  const {panelPopupMode} = usePanelContext();
  const isRenderingInPopup = useIsRenderingInPopup();
  const isFullscreenMode =
    isRenderingInPopup && panelPopupMode === PanelPopupMode.FULL_SCREEN;
  const nBuckets = isFullscreenMode ? 10000 : 1000;

  const {data, loading} = useBucketedData(
    runsLinePlotConfig,
    props.pageQuery,
    xDomainQuery,
    nBuckets
  );

  /**
   * When the data changes, we need to update the history data in the context. The reason is because for non-_step x-axis metrics we need to be able to convert the zoom coordinates (expressed in x-values that relate to the shown x-axis metric) back to _step so that we can feed them into the API. Avoiding circular dependencies in this is tricky, so to simplify the amount of business logic determining WHEN a new query gets made, we just syndicate each new data state up to the context so we can read it when we need it.
   */
  useEffect(() => {
    handleSetHistoryData(data, runsLinePlotConfig.xAxis ?? '');
  }, [data, handleSetHistoryData, runsLinePlotConfig.xAxis]);

  const {endPerfTimer} = startPerfTimer(`creating lines for outliers`);
  /**
   * When deriving the lines to show the run data on the plot we need to merge configuration data from multiple sources in order to draw the lines correctly.
   */
  const derivedLinesConfiguration = useDerivedLinesConfiguration({
    customRunColors: props.customRunColors,
    isGrouped: runsLinePlotConfig.isGrouped ?? false,
    runsLinePlotConfig,
    /**
     * I don't know what to do if there are multiple runsets - generally we can assume one
     */
    runSets: props.pageQuery.runSets ?? [],
    zoomTimestep: timeFactor,
  });

  const lines = useLines(derivedLinesConfiguration, data.histories.data);
  const linesWithTitleOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideSeriesTitles == null) {
      return lines;
    }
    return overrideLineTitles(
      lines as Line[],
      runsLinePlotConfig.overrideSeriesTitles,
      !isSingleRun
    );
  }, [lines, runsLinePlotConfig.overrideSeriesTitles, isSingleRun]);
  const linesWithColorOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideColors == null) {
      return linesWithTitleOverride;
    }
    return overrideLineColors(
      linesWithTitleOverride as Line[],
      runsLinePlotConfig.overrideColors,
      !isSingleRun
    );
  }, [linesWithTitleOverride, runsLinePlotConfig.overrideColors, isSingleRun]);

  const linesWithMarkOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideMarks == null) {
      return linesWithColorOverride;
    }
    return overrideMarks(
      linesWithColorOverride as Line[],
      runsLinePlotConfig.overrideMarks,
      !isSingleRun
    );
  }, [linesWithColorOverride, runsLinePlotConfig.overrideMarks, isSingleRun]);

  const linesWithWidthOverride = useMemo(() => {
    if (runsLinePlotConfig.overrideLineWidths == null) {
      return linesWithMarkOverride;
    }
    return overrideLineWidths(
      linesWithMarkOverride as Line[],
      runsLinePlotConfig.overrideLineWidths,
      !isSingleRun
    );
  }, [
    linesWithMarkOverride,
    runsLinePlotConfig.overrideLineWidths,
    isSingleRun,
  ]);

  endPerfTimer();

  if (props.configMode) {
    return (
      <ConfigWrapper
        {...props}
        // @ts-ignore
        data={data}
        featureSupport={featureSupport}
        // @ts-ignore
        lines={linesWithWidthOverride}
        lineCount={lines.length}
        loading={loading}
      />
    );
  }
  return (
    <GraphWrapper
      {...props}
      // @ts-ignore
      data={data}
      lineCount={lines.length}
      // @ts-ignore
      lines={linesWithWidthOverride}
      loading={loading}
      runNameTruncationType={
        props.pageQuery.runSets?.[0]?.runNameTruncationType
      }
    />
  );
};

export const PanelOutliersRunsLinePlot: React.FC<PanelRunsLinePlotProps> = memo(
  PanelOutliersRunsLinePlotComp
);
PanelOutliersRunsLinePlot.displayName = 'PanelOutliersRunsLinePlot';
